import { ChevronDown, ChevronRight } from "lucide-react";

import type { JDDetail, JDSkill } from "@/api/jd-queries";
import { Button } from "@/components/ui/button";
import { Combobox, Select } from "@/components/ui/input";
import { useDraft } from "@/lib/jd-draft-state";
import { isMappable, skillGroup } from "@/lib/jd-steps";
import { cn } from "@/lib/utils";

/**
 * One skill on the Skills step — DESIGN §20.5 step 4. A row reads as plain text and opens to edit:
 * with every control in every row the list was too dense to scan, and most skills are right as the
 * analysis left them. The editor holds importance, type, stack and concepts; its changes join the
 * page's unsaved changes like everything else.
 */

// Skill · Type · Importance · Maps to · Questions · chevron. Plain text, so it fits without scrolling.
export const SKILL_COLS =
  "grid grid-cols-[minmax(0,1.5fr)_90px_80px_minmax(0,1.2fr)_96px_16px] items-center gap-3 min-w-[640px]";

const IMPORTANCES = ["must", "nice", "other"] as const;
const CATEGORIES = ["technical", "domain", "soft", "other"] as const;
const GAP = "";
const MAPPED_BY: Record<string, string> = {
  model: "Matched by the AI when the JD was read.",
  alias: "Matched by a skill alias — someone mapped this wording before.",
  person: "Mapped by a person.",
};
const cap = "text-bank-muted mb-1 block text-[11.5px] font-medium";

export function ImportanceSwitch({
  value,
  onChange,
  label,
}: {
  value: JDSkill["importance"];
  onChange: (value: JDSkill["importance"]) => void;
  label: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="border-bank-border-strong bg-bank-surface inline-flex overflow-hidden rounded-md border"
    >
      {IMPORTANCES.map((i) => (
        <button
          key={i}
          type="button"
          aria-pressed={value === i}
          onClick={() => onChange(i)}
          className={cn(
            "border-bank-border h-[30px] border-r px-3 text-xs capitalize last:border-r-0",
            "focus-visible:ring-bank-accent focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset",
            value === i
              ? "bg-bank-accent-strong text-bank-on-accent font-semibold"
              : "text-bank-muted hover:bg-bank-surface-2",
          )}
        >
          {i}
        </button>
      ))}
    </div>
  );
}

/** The skill's stack, or "keyword: x" for a keyword match, with any concept limit after it. */
function mapsTo(skill: JDSkill): string {
  if (!isMappable(skill)) return "—";
  const where = skill.stack_path ?? (skill.entity ? `keyword: ${skill.entity}` : "not mapped");
  const n = skill.concepts.length;
  return n ? `${where} · ${n} concept${n === 1 ? "" : "s"}` : where;
}

export function StackPicker({
  jd,
  skill,
  onConcepts,
}: {
  jd: JDDetail;
  skill: JDSkill;
  onConcepts: () => void;
}) {
  const draft = useDraft();
  const stacks = skill.category === "technical" ? jd.technical_stacks : jd.domain_stacks;
  const id = `map-${skill.id}`;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label htmlFor={id} className="sr-only">
        Stack for {skill.name}
      </label>
      <Combobox
        id={id}
        className="w-60"
        value={skill.stack_path ?? GAP}
        onChange={(value) => draft.patchSkill(skill.id, { stack_path: value || null })}
        options={[{ value: GAP, label: "Choose a stack…" }, ...stacks.map((s) => ({ value: s, label: s }))]}
      />
      {skill.stack_path && (
        <Button
          size="sm"
          variant="ghost"
          title="Limit this skill to some of its stack's concepts. Nothing chosen means every concept."
          onClick={onConcepts}
        >
          {skill.concepts.length === 0
            ? "All concepts"
            : `${skill.concepts.length} concept${skill.concepts.length === 1 ? "" : "s"}`}
          <ChevronRight className="size-3.5" aria-hidden="true" />
        </Button>
      )}
    </div>
  );
}

/** Everything about one skill that can change, opened from its row. */
export function SkillEditor({
  jd,
  skill,
  canManage,
  withStack,
  onConcepts,
}: {
  jd: JDDetail;
  skill: JDSkill;
  canManage: boolean;
  withStack: boolean;
  onConcepts: () => void;
}) {
  const draft = useDraft();
  const group = skillGroup(skill);
  return (
    <div className="bg-bank-surface-2 border-bank-border space-y-3 border-t px-4 py-3">
      {skill.raw_text !== skill.name && (
        <p className="text-[12.5px]">
          <span className="text-bank-muted">The JD says </span>“{skill.raw_text}”
        </p>
      )}
      {canManage && (
        <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
          <div>
            <span className={cap}>Importance</span>
            <ImportanceSwitch
              label={`Importance of ${skill.name}`}
              value={skill.importance}
              onChange={(importance) => draft.patchSkill(skill.id, { importance })}
            />
          </div>
          <div>
            <span className={cap}>Type</span>
            <Select
              aria-label={`Type of ${skill.name}`}
              className="w-32 capitalize"
              value={skill.category}
              onChange={(e) =>
                draft.patchSkill(skill.id, { category: e.target.value as JDSkill["category"] })
              }
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </div>
          {withStack && isMappable(skill) && group !== "ignored" && (
            <div>
              <span className={cap}>Stack in the bank</span>
              <StackPicker jd={jd} skill={skill} onConcepts={onConcepts} />
            </div>
          )}
          <div className="ml-auto flex gap-1.5">
            {draft.skillPatched(skill.id) && (
              <Button size="sm" variant="ghost" onClick={() => draft.resetSkill(skill.id)}>
                Undo changes
              </Button>
            )}
            {group === "ignored" ? (
              <Button size="sm" onClick={() => draft.patchSkill(skill.id, { status: "restore" })}>
                Restore
              </Button>
            ) : (
              isMappable(skill) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => draft.patchSkill(skill.id, { status: "ignored" })}
                >
                  Ignore this skill
                </Button>
              )
            )}
          </div>
        </div>
      )}
      <p className="text-bank-muted text-xs">
        {skill.stack_path && skill.mapped_by ? `${MAPPED_BY[skill.mapped_by] ?? ""} ` : ""}
        {/* §20.9: importance is a weight on the stack, not a filter on the skill. */}
        {group === "unmappable"
          ? "Soft and other skills need no stack — the soft-skill and problem-solving parts of the interview cover them."
          : group === "ignored"
            ? "Left out of interviews and of the warnings."
            : "Must skills pull more of the technical questions toward their stack than Nice or Other ones."}
      </p>
    </div>
  );
}

/** A skill as one line of text; clicking it opens the editor beneath. */
export function SkillLine({
  jd,
  saved,
  open,
  onToggle,
  canManage,
  onConcepts,
}: {
  jd: JDDetail;
  saved: JDSkill;
  open: boolean;
  onToggle: () => void;
  canManage: boolean;
  onConcepts: () => void;
}) {
  const draft = useDraft();
  const skill = draft.skill(saved);
  const group = skillGroup(skill);
  const total = skill.concept_options.reduce((n, o) => n + o.questions, 0);
  const limited = skill.concepts.length > 0 && total > 0;
  const muted = group === "ignored" || group === "unmappable";
  // The count is the server's, for the saved stack and concepts; a new choice is counted on save.
  const stale = skill.stack_path !== saved.stack_path || skill.concepts.join() !== saved.concepts.join();

  return (
    <div className="border-bank-border border-t first:border-t-0">
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className={cn(
          SKILL_COLS,
          "hover:bg-bank-surface-2 w-full px-4 py-2.5 text-left",
          "focus-visible:ring-bank-accent focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset",
          open && "bg-bank-surface-2",
        )}
      >
        <span className="flex min-w-0 items-center gap-2">
          <span
            className={cn(
              "truncate text-[13.5px] font-medium",
              group === "ignored" && "text-bank-muted line-through",
            )}
          >
            {skill.name}
          </span>
          {draft.skillPatched(skill.id) && (
            <span className="text-bank-attention-ink shrink-0 text-[11px]">edited</span>
          )}
        </span>
        <span className="text-bank-muted text-[12.5px] capitalize">{skill.category}</span>
        <span className={cn("text-[12.5px] capitalize", skill.importance !== "must" && "text-bank-muted")}>
          {skill.importance}
        </span>
        <span className={cn("truncate font-mono text-[12px]", muted && "text-bank-muted")}>
          {mapsTo(skill)}
        </span>
        <span className="text-right font-mono text-[12px] tabular-nums">
          {stale && isMappable(skill) ? (
            <span className="text-bank-muted font-sans text-[11.5px]">on save</span>
          ) : isMappable(skill) && group !== "ignored" ? (
            <>
              {skill.questions}
              {limited && <span className="text-bank-muted"> of {total}</span>}
            </>
          ) : (
            <span className="text-bank-muted">—</span>
          )}
        </span>
        <ChevronDown
          className={cn("text-bank-muted size-4 transition-transform", open && "rotate-180")}
          aria-hidden="true"
        />
      </button>
      {open && <SkillEditor jd={jd} skill={skill} canManage={canManage} withStack onConcepts={onConcepts} />}
    </div>
  );
}
